import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { IProcessDetailResponse } from '../../models/processes.models';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CardSelectComponent,
	ISelect,
	ModalComponent,
	AlertComponent,
	TileComponent,
	CheckboxGroupComponent,
	TextareaComponent,
} from '@tecalisdev/infinity';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { IIdentityBlockPayload } from '../../../identities/models/identities.models';

@Component({
	selector: 'app-modal-reject-process',
	imports: [
		ModalComponent,
		TranslatePipe,
		ReactiveFormsModule,
		CardSelectComponent,
		AlertComponent,
		TileComponent,
		CheckboxGroupComponent,
		TextareaComponent,
	],
	templateUrl: './modal-reject-process.component.html',
	styleUrl: './modal-reject-process.component.scss',
})
export class ModalRejectProcessComponent implements OnInit {
	@Input() id: string;
	@Input() locks?: IProcessDetailResponse['locks'];
	@Input() showOcrBlockOption: boolean = true;
	@Input() showFaceBlockOption: boolean = true;
	@Output() closeEvent = new EventEmitter<undefined | Partial<IIdentityBlockPayload>>();

	private trans = inject(TranslateService);

	public checkboxOptions: ISelect[] = [];
	public blockedIdentityParams: string[] = [];
	public showOtherReason: boolean = false;
	public errorMsg: string = '';
	public blockParameters = new FormGroup({
		ocr: new FormControl(false),
		ip: new FormControl(false),
		face: new FormControl(false),
	});
	private reasonsControl = new FormControl([], Validators.required);
	public otherReasonControl = new FormControl(null);
	public form: FormGroup = new FormGroup({
		blockParameters: this.blockParameters,
		reasons: this.reasonsControl,
		otherReason: this.otherReasonControl,
	});

	constructor() {
		this.reasonsControl.valueChanges.subscribe((selectedReasons: ISelect[]) => {
			// Mostrar u ocultar el campo de "otherReason" según si "other_reasons" está seleccionado
			const isOtherReasonSelected = selectedReasons.find((reason: ISelect) => reason?.id === 'other_reasons');
			if (isOtherReasonSelected && !this.showOtherReason) {
				this.showOtherReason = true;
				this.otherReasonControl?.markAsUntouched();
				this.otherReasonControl?.setValidators(Validators.required);
				this.otherReasonControl?.updateValueAndValidity();
			} else if (!isOtherReasonSelected && this.showOtherReason) {
				this.showOtherReason = false;
				this.otherReasonControl?.clearValidators();
				this.otherReasonControl?.setValue(null);
				this.otherReasonControl?.updateValueAndValidity();
			}
		});
	}

	ngOnInit(): void {
		this.getIdentityBlocks();
		this.prepareCheckboxOptions();
	}

	public closeModal(type: string) {
		if (type === 'buttonPrimary') {
			if (this.form.invalid) {
				this.errorMsg = this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.AT_LEAST_ONE_PARAMETER_REQUIRED');
				this.form.markAllAsTouched();
				return;
			}
			this.closeEvent.emit(this.getBody());
			return;
		}

		this.closeEvent.emit();
	}

	private getBody(): Partial<IIdentityBlockPayload> {
		const selectedReasons = this.reasonsControl.value as ISelect[];
		const reasonsPayload: { [key: string]: boolean | string } = {
			invalid_document: false,
			document_data_not_valid: false,
			expired_document: false,
			damaged_or_unreadable_document: false,
			front_and_back_mismatch: false,
			required_fields_not_readable: false,
			address_not_verifiable: false,
			document_photo_not_matching_face: false,
			other_reason: '',
		};

		selectedReasons.forEach((reason: ISelect) => {
			if (reason.id === 'other_reasons') {
				reasonsPayload['other_reason'] = this.otherReasonControl.value;
			} else {
				reasonsPayload[reason.id] = true;
			}
		});

		const payload: Partial<IIdentityBlockPayload> = {
			reasons: JSON.stringify(reasonsPayload),
		};

		if (this.form.value.blockParameters.ip) {
			payload.blocked_ip = true;
		}

		if (this.form.value.blockParameters.ocr) {
			payload.blocked_identifier = true;
		}

		if (this.form.value.blockParameters.face) {
			payload.blocked_face = true;
		}

		return payload;
	}

	private getIdentityBlocks(): void {
		if (this.locks) {
			this.blockedIdentityParams = [];
			if (
				this.showOcrBlockOption &&
				this.locks?.blocked_identifiers &&
				this.locks?.blocked_identifiers.length > 0
			) {
				this.blockedIdentityParams.push('ocr');
			}
			if (this.locks?.blocked_ips && this.locks?.blocked_ips.length > 0) {
				this.blockedIdentityParams.push('ip');
			}
			if (this.showFaceBlockOption && this.locks?.blocked_faces && this.locks?.blocked_faces.length > 0) {
				this.blockedIdentityParams.push('face');
			}
		}

		if (this.blockedIdentityParams.length > 0) {
			this.blockParameters.patchValue({
				ocr: this.blockedIdentityParams.includes('ocr'),
				ip: this.blockedIdentityParams.includes('ip'),
				face: this.blockedIdentityParams.includes('face'),
			});

			// Deshabilitar los checkbox que ya están bloqueados
			this.blockedIdentityParams.forEach((block) => {
				this.blockParameters.get(block)?.disable();
			});
		}
	}

	private prepareCheckboxOptions() {
		this.checkboxOptions = [
			{
				id: 'invalid_document',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.INVALID_DOCUMENT'),
			},
			{
				id: 'document_data_not_valid',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.DATA_REQUIREMENTS_NOT_MET'),
			},
			{
				id: 'expired_document',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.EXPIRED_DOCUMENT'),
			},
			{
				id: 'damaged_or_unreadable_document',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.DAMAGED_DOCUMENT'),
			},
			{
				id: 'front_and_back_mismatch',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.FRONT_BACK_MISMATCH'),
			},
			{
				id: 'required_fields_not_readable',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.UNREADABLE_REQUIRED_FIELDS'),
			},
			{
				id: 'address_not_verifiable',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.UNVERIFIABLE_ADDRESS'),
			},
			{
				id: 'document_photo_not_matching_face',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.PHOTO_FACE_MISMATCH'),
			},
			{
				id: 'other_reasons',
				label: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.OTHER_REASONS'),
			},
		];
	}
}
